import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';
import ChamaDashboardTabBar from '../../components/ChamaDashboardTabBar';

interface Chama {
  id: string;
  name: string;
  description?: string;
  category?: string;
  type?: string;
  contributionAmount?: number;
  contributionFrequency?: string;
  maxMembers?: number;
  currentMembers?: number;
  totalFunds?: number;
}

const ChamaDashboard: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [activeTab, setActiveTab] = useState('overview');
  const [chama, setChama] = useState<Chama | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (chamaId) {
      loadChamaData();
    }
  }, [chamaId]);

  const loadChamaData = async () => {
    if (!chamaId) return;

    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaDashboard: No auth token found');
        alert('Authentication required. Please log in again.');
        return;
      }

      // Real API call to get chama details
      const response = await fetch(API_ENDPOINTS.CHAMA_DETAILS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ ChamaDashboard: Failed to load chama details:', errorData);
        alert(`Failed to load chama details: ${errorData.error || 'Unknown error'}`);
        return;
      }

      const data = await response.json();
      if (data.success && data.data) {
        setChama(data.data);
      } else {
        console.error('❌ ChamaDashboard: Invalid chama data structure:', data);
        alert('Invalid chama data received from server');
      }
    } catch (error) {
      console.error('❌ ChamaDashboard: Error loading chama details:', error);
      alert('Network error: Unable to connect to the server. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const renderOverview = () => (
    <div style={styles.section}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Overview</h2>

      {/* Chama Info Card */}
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div style={styles.cardHeader}>
          <div style={styles.chamaAvatar}>
            <span style={{ fontSize: '24px' }}>👥</span>
          </div>
          <div style={styles.chamaInfo}>
            <h3 style={{ ...styles.chamaName, color: colors.text }}>{chama?.name}</h3>
            <p style={{ ...styles.chamaDescription, color: colors.textSecondary }}>
              {chama?.description || 'No description available'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.statIcon}>
            <span>💰</span>
          </div>
          <div style={styles.statInfo}>
            <span style={{ ...styles.statValue, color: colors.text }}>
              {formatCurrency(chama?.totalFunds || 0)}
            </span>
            <span style={{ ...styles.statLabel, color: colors.textSecondary }}>Total Funds</span>
          </div>
        </div>

        <div style={{ ...styles.statCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.statIcon}>
            <span>👥</span>
          </div>
          <div style={styles.statInfo}>
            <span style={{ ...styles.statValue, color: colors.text }}>
              {chama?.currentMembers}/{chama?.maxMembers}
            </span>
            <span style={{ ...styles.statLabel, color: colors.textSecondary }}>Members</span>
          </div>
        </div>

        <div style={{ ...styles.statCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.statIcon}>
            <span>📅</span>
          </div>
          <div style={styles.statInfo}>
            <span style={{ ...styles.statValue, color: colors.text }}>5</span>
            <span style={{ ...styles.statLabel, color: colors.textSecondary }}>Meetings</span>
          </div>
        </div>

        <div style={{ ...styles.statCard, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
          <div style={styles.statIcon}>
            <span>💳</span>
          </div>
          <div style={styles.statInfo}>
            <span style={{ ...styles.statValue, color: colors.text }}>
              {formatCurrency(chama?.contributionAmount || 0)}
            </span>
            <span style={{ ...styles.statLabel, color: colors.textSecondary }}>
              {chama?.contributionFrequency}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <h3 style={{ ...styles.cardTitle, color: colors.text }}>Quick Actions</h3>
        <div style={{ ...styles.quickActionsGrid, padding: spacing.lg }}>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/members`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: colors.secondary }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                👥
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Members
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/contribute`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: colors.primary }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                💰
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Contribute
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/transactions`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: colors.success }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                📄
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Transactions
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/loans`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#6366F1' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                💳
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Loans
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/meetings`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#8B5CF6' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                📅
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Meetings
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/merry-go-round`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#F59E0B' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                🔄
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Merry-Go-Round
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/welfare`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#EC4899' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                ❤️
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Welfare
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/shares-dividends`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#10B981' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                📊
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Shares & Dividends
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/polls-voting`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#8B5CF6' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                🗳️
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Polls & Voting
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/account-management`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#F59E0B' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                🏢
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Account Management
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/chat`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#3B82F6' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                💬
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Group Chat
            </span>
          </div>
          <div
            style={styles.quickActionItem}
            onClick={() => window.location.href = `/dashboard/chama/${chama?.id}/settings`}
          >
            <div style={{ ...styles.quickActionIcon, backgroundColor: '#6B7280' }}>
              <span style={{ fontSize: '24px', color: '#FFFFFF' }}>
                ⚙️
              </span>
            </div>
            <span style={{ ...styles.quickActionText, color: colors.text }}>
              Settings
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMembers = () => (
    <div style={styles.section}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Members</h2>
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <p style={{ ...styles.placeholderText, color: colors.textSecondary }}>
          Members list will be displayed here. This section shows all chama members with their roles and contribution status.
        </p>
      </div>
    </div>
  );

  const renderContributions = () => (
    <div style={styles.section}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Contributions</h2>
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <p style={{ ...styles.placeholderText, color: colors.textSecondary }}>
          Contributions section will show contribution history, pending payments, and contribution analytics.
        </p>
      </div>
    </div>
  );

  const renderTransactions = () => (
    <div style={styles.section}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Transactions</h2>
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <p style={{ ...styles.placeholderText, color: colors.textSecondary }}>
          Transaction history will be displayed here, including all financial transactions within the chama.
        </p>
      </div>
    </div>
  );

  const renderLoans = () => (
    <div style={styles.section}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Loans</h2>
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <p style={{ ...styles.placeholderText, color: colors.textSecondary }}>
          Loans section will show active loans, loan applications, and loan repayment tracking.
        </p>
      </div>
    </div>
  );

  const renderMeetings = () => (
    <div style={styles.section}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Meetings</h2>
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <p style={{ ...styles.placeholderText, color: colors.textSecondary }}>
          Meetings section will display upcoming meetings, meeting history, and meeting management tools.
        </p>
      </div>
    </div>
  );

  const renderMerryGoRound = () => (
    <div style={styles.section}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Merry-Go-Round</h2>
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <p style={{ ...styles.placeholderText, color: colors.textSecondary }}>
          Merry-Go-Round section will show rotating savings groups, payout schedules, and member rotation tracking.
        </p>
      </div>
    </div>
  );

  const renderWelfare = () => (
    <div style={styles.section}>
      <h2 style={{ ...styles.sectionTitle, color: colors.text }}>Welfare</h2>
      <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <p style={{ ...styles.placeholderText, color: colors.textSecondary }}>
          Welfare section will show community support programs, emergency funds, and welfare contributions.
        </p>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'members':
        return renderMembers();
      case 'contributions':
        return renderContributions();
      case 'transactions':
        return renderTransactions();
      case 'loans':
        return renderLoans();
      case 'meetings':
        return renderMeetings();
      case 'merry-go-round':
        return renderMerryGoRound();
      case 'welfare':
        return renderWelfare();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading chama dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title={chama?.name || 'Chama Dashboard'}
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {renderTabContent()}
        </div>
      </div>

      <ChamaDashboardTabBar
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80, // Space for tab bar
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80, // Space for fixed header
    paddingBottom: 80, // Space for fixed tab bar
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.md,
  },
  card: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    ...getShadowStyle('sm'),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  chamaAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  chamaInfo: {
    flex: 1,
  },
  chamaName: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.xs,
  },
  chamaDescription: {
    fontSize: typography.fontSize.sm,
    margin: 0,
    lineHeight: 1.4,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  statCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    ...getShadowStyle('sm'),
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: borderRadius.lg,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  statInfo: {
    flex: 1,
  },
  statValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    display: 'block',
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    display: 'block',
    marginTop: spacing.xs / 2,
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.md,
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
    gap: spacing.md,
    justifyItems: 'center',
    padding: spacing.md,
  },
  quickActionItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: spacing.sm,
    cursor: 'pointer',
  },
  quickActionIcon: {
    width: 48,
    height: 48,
    borderRadius: borderRadius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  quickActionText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    textAlign: 'center',
    marginTop: spacing.xs,
    lineHeight: '1.2',
  },
  placeholderText: {
    fontSize: typography.fontSize.base,
    textAlign: 'center',
    padding: spacing.xl,
    margin: 0,
  },
};

export default ChamaDashboard;